import { STATUS } from "./statuses";

/**
 * Engineering & Technology Services — four primary divisions.
 * Data-driven; UI should not hardcode service content.
 */

export const SERVICE_DIVISIONS = [
  {
    id: "industrial-automation",
    code: "S-01",
    name: "Industrial Automation",
    icon: "Factory",
    status: STATUS.ACTIVE,
    description:
      "PLC, SCADA, HMI, industrial networking and automation software for manufacturing and process environments.",
    industries: [
      "Manufacturing",
      "Steel & Metallurgy",
      "Automotive & Ancillary",
      "Process Industries",
      "OEM Machine Builders",
    ],
  },
  {
    id: "electrical-engineering",
    code: "S-02",
    name: "Electrical Engineering",
    icon: "Zap",
    status: STATUS.ACTIVE,
    description:
      "LT panels, MCC/PCC systems, instrumentation, industrial wiring and commissioning for plant electrical infrastructure.",
    industries: [
      "Manufacturing",
      "Power & Energy",
      "Infrastructure",
      "Water & Utilities",
      "Oil & Gas",
    ],
  },
  {
    id: "electronics-engineering",
    code: "S-03",
    name: "Electronics Engineering",
    icon: "CircuitBoard",
    status: STATUS.ACTIVE,
    description:
      "Power electronics, custom electronics and industrial electronics for practical, manufacturable industrial applications.",
    industries: [
      "OEM Machine Builders",
      "Smart Buildings",
      "Manufacturing",
      "Renewable Energy",
    ],
  },
  {
    id: "digital-platforms",
    code: "S-04",
    name: "Digital Platforms",
    icon: "Boxes",
    status: STATUS.COMING_SOON,
    description:
      "Industrial software, engineering dashboards and long-term digital platform initiatives including DEBSUN GENESYS.",
    industries: [
      "Manufacturing",
      "Process Industries",
      "Steel & Metallurgy",
      "Pharma & Chemical",
    ],
  },
];

/** Individual engineering services within each division. */
export const SERVICES = [
  // Industrial Automation
  {
    id: "plc-programming",
    divisionId: "industrial-automation",
    name: "PLC Programming",
    icon: "Cpu",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Design, development and commissioning of PLC control logic for machines, lines and process systems.",
    applications: [
      "Machine control",
      "Line automation",
      "Process sequencing",
      "Retrofits",
    ],
    industries: [
      "Manufacturing",
      "Automotive & Ancillary",
      "OEM Machine Builders",
      "Process Industries",
    ],
  },
  {
    id: "scada-development",
    divisionId: "industrial-automation",
    name: "SCADA Development",
    icon: "Monitor",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Supervisory control and data acquisition systems for plant visibility, alarm management and operational control.",
    applications: [
      "Plant monitoring",
      "Central control rooms",
      "Alarm & event management",
      "Historical trending",
    ],
    industries: [
      "Manufacturing",
      "Water & Utilities",
      "Power & Energy",
      "Oil & Gas",
    ],
  },
  {
    id: "hmi-development",
    divisionId: "industrial-automation",
    name: "HMI Development",
    icon: "LayoutDashboard",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Operator interface design and configuration for clear, reliable machine and process interaction.",
    applications: [
      "Machine HMIs",
      "Line operator stations",
      "Local control panels",
      "Faceplate libraries",
    ],
    industries: [
      "OEM Machine Builders",
      "Manufacturing",
      "Automotive & Ancillary",
    ],
  },
  {
    id: "industrial-networking",
    divisionId: "industrial-automation",
    name: "Industrial Networking",
    icon: "Network",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Design and implementation of industrial communication networks for reliable plant connectivity.",
    applications: [
      "Profinet / EtherNet/IP",
      "Modbus TCP/RTU",
      "Plant backbone design",
      "Network segmentation",
    ],
    industries: [
      "Manufacturing",
      "Process Industries",
      "Infrastructure",
    ],
  },
  {
    id: "industrial-iot",
    divisionId: "industrial-automation",
    name: "Industrial IoT",
    icon: "Radio",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Edge connectivity and telemetry bridging brownfield equipment to modern industrial data platforms.",
    applications: [
      "Edge gateways",
      "Machine telemetry",
      "Energy monitoring",
      "Legacy PLC bridging",
    ],
    industries: [
      "Manufacturing",
      "Steel & Metallurgy",
      "Pharma & Chemical",
    ],
  },
  {
    id: "mes",
    divisionId: "industrial-automation",
    name: "Manufacturing Execution Systems",
    icon: "ClipboardList",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "MES workflows connecting production operations, traceability and plant-floor execution.",
    applications: [
      "Production tracking",
      "Work-order execution",
      "Traceability",
      "OEE visibility",
    ],
    industries: [
      "Manufacturing",
      "Automotive & Ancillary",
      "Pharma & Chemical",
      "Food & Beverage",
    ],
  },
  {
    id: "digital-twin",
    divisionId: "industrial-automation",
    name: "Digital Twin Development",
    icon: "Box",
    status: STATUS.COMING_SOON,
    quoteEnabled: false,
    description:
      "Digital representation of equipment and processes for simulation, diagnostics and operational insight.",
    applications: [
      "Process simulation",
      "What-if analysis",
      "Commissioning support",
      "Performance studies",
    ],
    industries: [
      "Manufacturing",
      "Process Industries",
      "Steel & Metallurgy",
    ],
  },
  {
    id: "control-panels",
    divisionId: "industrial-automation",
    name: "Control Panels",
    icon: "PanelTop",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Design, assembly and testing of automation and control panels for industrial applications.",
    applications: [
      "PLC panels",
      "Drive panels",
      "Local control stations",
      "OEM panel packages",
    ],
    industries: [
      "OEM Machine Builders",
      "Manufacturing",
      "Infrastructure",
    ],
  },
  {
    id: "machine-vision",
    divisionId: "industrial-automation",
    name: "Machine Vision",
    icon: "ScanEye",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Vision-based inspection and guidance systems for quality and process control.",
    applications: [
      "Quality inspection",
      "Part presence",
      "Measurement",
      "Guidance",
    ],
    industries: [
      "Automotive & Ancillary",
      "Manufacturing",
      "Pharma & Chemical",
    ],
  },
  {
    id: "robot-programming",
    divisionId: "industrial-automation",
    name: "Robot Programming & Teaching",
    icon: "Bot",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Robot programming, teaching and integration for industrial handling and process cells.",
    applications: [
      "Pick and place",
      "Welding cells",
      "Material handling",
      "Cell integration",
    ],
    industries: [
      "Automotive & Ancillary",
      "Manufacturing",
      "OEM Machine Builders",
    ],
  },
  {
    id: "process-optimization",
    divisionId: "industrial-automation",
    name: "Process Optimization and Analytics",
    icon: "LineChart",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Data-driven analysis and optimisation of industrial processes for throughput, quality and energy.",
    applications: [
      "Process analytics",
      "Energy optimisation",
      "Bottleneck analysis",
      "KPI dashboards",
    ],
    industries: [
      "Manufacturing",
      "Process Industries",
      "Steel & Metallurgy",
    ],
  },
  {
    id: "industrial-software",
    divisionId: "industrial-automation",
    name: "Industrial Software Development",
    icon: "Code2",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Custom industrial software for operations, engineering workflows and plant integration.",
    applications: [
      "Engineering tools",
      "Operations software",
      "Integration layers",
      "Custom HMIs",
    ],
    industries: [
      "Manufacturing",
      "Process Industries",
      "Infrastructure",
    ],
  },

  // Electrical Engineering
  {
    id: "lt-panels",
    divisionId: "electrical-engineering",
    name: "LT Panels",
    icon: "Bolt",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Low-tension distribution panels engineered for industrial power distribution and plant feeders.",
    applications: [
      "Main distribution",
      "Sub-distribution",
      "Feeder panels",
      "Plant electrical rooms",
    ],
    industries: [
      "Manufacturing",
      "Infrastructure",
      "Power & Energy",
    ],
  },
  {
    id: "mcc-panels",
    divisionId: "electrical-engineering",
    name: "MCC Panels",
    icon: "Server",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Motor control centres for organised, maintainable control of industrial motor loads.",
    applications: [
      "Motor starters",
      "Group control",
      "Drive integration",
      "Plant MCC rooms",
    ],
    industries: [
      "Manufacturing",
      "Water & Utilities",
      "Process Industries",
    ],
  },
  {
    id: "pcc-panels",
    divisionId: "electrical-engineering",
    name: "PCC Panels",
    icon: "Gauge",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Power control centres for reliable distribution and protection of industrial electrical systems.",
    applications: [
      "Incoming power control",
      "Bus distribution",
      "Protection schemes",
      "Plant power rooms",
    ],
    industries: [
      "Manufacturing",
      "Steel & Metallurgy",
      "Infrastructure",
    ],
  },
  {
    id: "instrumentation",
    divisionId: "electrical-engineering",
    name: "Instrumentation",
    icon: "Activity",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Field instrumentation selection, integration and commissioning for process measurement and control.",
    applications: [
      "Flow / pressure / temperature",
      "Level measurement",
      "Signal conditioning",
      "Loop checking",
    ],
    industries: [
      "Process Industries",
      "Oil & Gas",
      "Water & Utilities",
      "Pharma & Chemical",
    ],
  },
  {
    id: "industrial-wiring",
    divisionId: "electrical-engineering",
    name: "Industrial Wiring",
    icon: "Cable",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Industrial cabling and wiring practices for power, control and instrumentation circuits.",
    applications: [
      "Power cabling",
      "Control wiring",
      "Instrument loops",
      "Panel field terminations",
    ],
    industries: [
      "Manufacturing",
      "Infrastructure",
      "OEM Machine Builders",
    ],
  },
  {
    id: "commissioning",
    divisionId: "electrical-engineering",
    name: "Commissioning",
    icon: "CheckCircle2",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Systematic electrical and automation commissioning through to operational handover.",
    applications: [
      "Pre-commissioning checks",
      "Energisation",
      "Functional testing",
      "Handover documentation",
    ],
    industries: [
      "Manufacturing",
      "Infrastructure",
      "Power & Energy",
      "Process Industries",
    ],
  },

  // Electronics Engineering — current commercial capability only
  {
    id: "power-electronics",
    divisionId: "electronics-engineering",
    name: "Power Electronics",
    icon: "Zap",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Power conversion and drive-related electronics for industrial energy and motor applications.",
    applications: [
      "Power conversion",
      "Drive interfaces",
      "Power supplies",
      "Energy systems",
    ],
    industries: [
      "Power & Energy",
      "Renewable Energy",
      "Manufacturing",
    ],
  },
  {
    id: "custom-electronics",
    divisionId: "electronics-engineering",
    name: "Custom Electronics",
    icon: "Wrench",
    status: STATUS.CUSTOMISABLE,
    quoteEnabled: true,
    description:
      "Application-specific electronic solutions engineered to defined industrial requirements.",
    applications: [
      "Custom controllers",
      "Special-purpose boards",
      "Interface adapters",
      "Product electronics",
    ],
    industries: [
      "OEM Machine Builders",
      "Manufacturing",
      "Smart Buildings",
    ],
  },
  {
    id: "industrial-electronics",
    divisionId: "electronics-engineering",
    name: "Industrial Electronics",
    icon: "Cpu",
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    description:
      "Industrial-grade electronic assemblies designed for harsh operating environments.",
    applications: [
      "Control electronics",
      "Interface modules",
      "Signal boards",
      "Protection circuits",
    ],
    industries: [
      "Manufacturing",
      "OEM Machine Builders",
      "Infrastructure",
    ],
  },

  // Digital Platforms
  {
    id: "debsun-genesys",
    divisionId: "digital-platforms",
    name: "DEBSUN GENESYS",
    icon: "Server",
    status: STATUS.COMING_SOON,
    quoteEnabled: false,
    href: "/genesys",
    description:
      "Long-term industrial intelligence initiative unifying engineering workflows, operations and decision support.",
    applications: [
      "Industrial intelligence",
      "Factory operations",
      "Engineering collaboration",
      "Production analytics",
    ],
    industries: [
      "Manufacturing",
      "Process Industries",
      "Steel & Metallurgy",
    ],
  },
  {
    id: "factorysim",
    divisionId: "digital-platforms",
    name: "FactorySim",
    icon: "Factory",
    status: STATUS.COMING_SOON,
    quoteEnabled: false,
    description:
      "Factory simulation initiative for studying layouts, flows and operational scenarios.",
    applications: [
      "Layout studies",
      "Throughput simulation",
      "Scenario comparison",
      "Planning support",
    ],
    industries: [
      "Manufacturing",
      "Automotive & Ancillary",
    ],
  },
  {
    id: "industrial-ai",
    divisionId: "digital-platforms",
    name: "Industrial AI",
    icon: "BrainCircuit",
    status: STATUS.COMING_SOON,
    quoteEnabled: false,
    description:
      "Applied industrial intelligence research focused on engineering assistance and operational insight.",
    applications: [
      "Engineering assistance",
      "Operational insight",
      "Diagnostics support",
      "Decision support",
    ],
    industries: [
      "Manufacturing",
      "Process Industries",
      "Pharma & Chemical",
    ],
  },
  {
    id: "engineering-dashboards",
    divisionId: "digital-platforms",
    name: "Engineering Dashboards",
    icon: "LayoutDashboard",
    status: STATUS.UNDER_DEVELOPMENT,
    quoteEnabled: true,
    description:
      "Engineering and operations dashboards for clear visibility of plant and project data.",
    applications: [
      "KPI visualisation",
      "Project status views",
      "Operational summaries",
      "Engineering reports",
    ],
    industries: [
      "Manufacturing",
      "Infrastructure",
      "Process Industries",
    ],
  },
  {
    id: "digital-engineering-tools",
    divisionId: "digital-platforms",
    name: "Digital Engineering Tools",
    icon: "Hammer",
    status: STATUS.UNDER_DEVELOPMENT,
    quoteEnabled: true,
    description:
      "Digital tools that support engineering workflows, documentation and collaboration.",
    applications: [
      "Engineering utilities",
      "Workflow support",
      "Documentation aids",
      "Collaboration tools",
    ],
    industries: [
      "Manufacturing",
      "OEM Machine Builders",
      "Infrastructure",
    ],
  },
];

export function getDivisionById(id) {
  return SERVICE_DIVISIONS.find((d) => d.id === id) || null;
}

export function getServicesByDivision(divisionId) {
  return SERVICES.filter((s) => s.divisionId === divisionId);
}

export function getServiceById(id) {
  return SERVICES.find((s) => s.id === id) || null;
}

export function getQuoteEnabledServices() {
  return SERVICES.filter((s) => s.quoteEnabled);
}
